import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { getPropertyDetails as getRentPropertyDetails, getDisplayPrice as getRentDisplayPrice } from '../lib/services/rent-search';
import { getPropertyDetails as getSoldPropertyDetails, getDisplayPrice as getSoldDisplayPrice } from '../lib/services/sold-search';
import { getPropertyDetails as getUnifiedPropertyDetails } from '../lib/services/property-details';
import { enhancedFetch } from '../lib/utils/api-helpers';
import { Property } from '../lib/types/property';

import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { MapPin, BedDouble, Bath, Ruler, Calendar, Heart, Map as MapIcon, Receipt, Camera, ArrowLeft } from 'lucide-react';
// Removed unused maps constants import
import PropertyMap from '../components/PropertyMap';
import PhotoViewer from '../components/PhotoViewer';
import { filterZestimateDescriptions } from '../lib/utils';
import { useAppContext } from '../lib/AppContext';

// Import search caches to check for existing property data
import { propertyCache, restoreCacheFromLocalStorage, clearAllCaches } from "../lib/utils/centralized-cache";

// Import database backup functions for fallback when local cache is empty
import { getPropertiesFromDatabaseBackup, upsertEnhancedPropertyData } from '../lib/utils/centralized-cache';

// Removed unused enhancePropertyWithDetails helper

// REMOVED: getBuyPropertiesFromCache function - no longer needed since we skip database enhancement

export default function PropertyDetail() {
  const [location, setLocation] = useLocation();
  const id = location.split('/')[2]; // Extract property ID from URL
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const { favorites, toggleFavorite } = useAppContext();

  // Ensure the details page starts at the top on navigation
  useEffect(() => {
    try {
      // Scroll common app scroll container (flex-1 with overflow-y-auto)
      const appRoot = document.querySelector('.max-w-md.mx-auto.h-screen.flex.flex-col.bg-white.overflow-hidden.relative');
      const scrollEl = appRoot?.querySelector(':scope > div.flex-1') as HTMLElement | null;
      if (scrollEl && (scrollEl.scrollHeight > scrollEl.clientHeight)) {
        scrollEl.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch {}
    // Always also scroll window as a fallback
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch {}
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionTimeout, setDescriptionTimeout] = useState<NodeJS.Timeout | null>(null);

  // Handle photo click to open full-screen viewer
  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
    setPhotoViewerOpen(true);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (descriptionTimeout) {
        clearTimeout(descriptionTimeout);
      }
    };
  }, [descriptionTimeout]);


  // Update localStorage cache when property data is available
  useEffect(() => {
    if (property) {
      // If we have a property with correct data, update the localStorage cache
      if (property.description && property.description !== 'Property details available' && 
          (property.daysOnZillow || property.daysOnMarket || (property as any).days_on_zillow || 
           (property as any).timeOnZillow || (property as any).time_on_zillow)) {
        try {
          const storedListings = localStorage.getItem('homeFilters_listings');
          if (storedListings) {
            const listings = JSON.parse(storedListings);
            if (Array.isArray(listings)) {
              // Update the property in the cache
              const updatedListings = listings.map((p: Property) => 
                p.source_id === property.source_id || p.id === property.id ? property : p
              );
              localStorage.setItem('homeFilters_listings', JSON.stringify(updatedListings));
            }
          }
        } catch (error) {
          // Silently handle localStorage errors
        }
      }
    }
  }, [property]);

  /**
   * Enhance property in background without blocking the UI
   */
  const enhancePropertyInBackground = async (property: Property, propertyId: string): Promise<void> => {
    try {
      // Try API enhancement
      try {
        const detailsUrl = `/api/properties/details?zpid=${propertyId}`;
        const response = await enhancedFetch(detailsUrl);
        
        if (response.ok) {
          const detailsData = await response.json();
          
          // Process API data for enhancement - check multiple possible field names for days on market
          let processedDaysOnZillow: number | undefined = undefined;
          let processedTimeOnZillow: string | undefined = undefined;
          let processedDaysOnMarket: number | undefined = undefined;
          
          // Helper function to extract numeric value from string or number
          const extractNumericValue = (value: any): number | undefined => {
            if (typeof value === 'number' && value > 0) {
              return value;
            } else if (typeof value === 'string') {
              const match = value.match(/\d+/);
              return match ? parseInt(match[0], 10) : undefined;
            }
            return undefined;
          };
          
          // Check for daysOnZillow field
          const daysOnZillowValue = detailsData.data?.daysOnZillow || detailsData.daysOnZillow;
          processedDaysOnZillow = extractNumericValue(daysOnZillowValue);
          
          // Check for days_on_zillow field (alternative naming)
          if (!processedDaysOnZillow) {
            const daysOnZillowAltValue = detailsData.data?.days_on_zillow || detailsData.days_on_zillow;
            processedDaysOnZillow = extractNumericValue(daysOnZillowAltValue);
          }
          
          // Check for timeOnZillow field
          const timeOnZillowValue = detailsData.data?.timeOnZillow || detailsData.timeOnZillow;
          if (timeOnZillowValue) {
            processedTimeOnZillow = timeOnZillowValue;
            // Also try to extract numeric value from timeOnZillow
            if (!processedDaysOnZillow) {
              processedDaysOnZillow = extractNumericValue(timeOnZillowValue);
            }
          }
          
          // Check for time_on_zillow field (alternative naming)
          if (!processedTimeOnZillow) {
            const timeOnZillowAltValue = detailsData.data?.time_on_zillow || detailsData.time_on_zillow;
            if (timeOnZillowAltValue) {
              processedTimeOnZillow = timeOnZillowAltValue;
              // Also try to extract numeric value from time_on_zillow
              if (!processedDaysOnZillow) {
                processedDaysOnZillow = extractNumericValue(timeOnZillowAltValue);
              }
            }
          }
          
          // Check for daysOnMarket field
          const daysOnMarketValue = detailsData.data?.daysOnMarket || detailsData.daysOnMarket;
          processedDaysOnMarket = extractNumericValue(daysOnMarketValue);
          
          // Check for days_on_market field (alternative naming)
          if (!processedDaysOnMarket) {
            const daysOnMarketAltValue = detailsData.data?.days_on_market || detailsData.days_on_market;
            processedDaysOnMarket = extractNumericValue(daysOnMarketAltValue);
          }
          
          // Use the best available value for daysOnZillow
          if (processedDaysOnMarket && !processedDaysOnZillow) {
            processedDaysOnZillow = processedDaysOnMarket;
          }
          
          // Extract sold date from API response
          let soldDate = null;
          
          if (detailsData.data?.formattedChip?.additionalFacts && Array.isArray(detailsData.data.formattedChip.additionalFacts)) {
            const soldDateFact = detailsData.data.formattedChip.additionalFacts.find((fact: any) => 
              fact.elementType === 'soldOnDate' && fact.value?.fullValue
            );
            if (soldDateFact) {
              soldDate = soldDateFact.value.fullValue;
            }
          }
          
          // Fallback: Check additionalFacts directly (server-side approach)
          if (!soldDate && detailsData.data?.additionalFacts && Array.isArray(detailsData.data.additionalFacts)) {
            const soldDateFact = detailsData.data.additionalFacts.find((fact: any) => 
              fact.factLabel === 'Sold Date' || fact.factLabel === 'Sold'
            );
            if (soldDateFact) {
              soldDate = soldDateFact.value.fullValue;
            }
          }
          
          if (!soldDate) {
          }
          
          // Use centralized cache method for property enhancement
          const { enhancePropertyWithApiData } = await import('../lib/utils/centralized-cache');
          
          const enhancementData = {
            description: detailsData.data?.description || 
                        detailsData.description || 
                        detailsData.data?.propertyDescription ||
                        detailsData.data?.listingDescription ||
                        detailsData.data?.homeDescription,
            daysOnZillow: processedDaysOnZillow,
            timeOnZillow: processedTimeOnZillow,
            daysOnMarket: processedDaysOnMarket || processedDaysOnZillow,
            days_on_zillow: processedDaysOnZillow,
            time_on_zillow: processedTimeOnZillow,
            days_on_market: processedDaysOnMarket || processedDaysOnZillow,
            photos: detailsData.data?.photos || detailsData.photos || property.photos || [],
            // Add sold date information
            soldDate: soldDate,
            sold_date: soldDate,
            soldPropertyMetadata: {
              ...(property as any).soldPropertyMetadata,
              soldDate: soldDate
            }
          };
          
          
          const enhancedProperty = await enhancePropertyWithApiData(property, enhancementData);
          
          
          // Upsert enhanced data to database for future use
          upsertEnhancedPropertyData(enhancedProperty);
          
          setProperty(enhancedProperty);
          
          // Clear description loading state if we got a valid description
          if (enhancedProperty.description && 
              enhancedProperty.description !== 'Property details available' &&
              enhancedProperty.description !== 'No description available' &&
              enhancedProperty.description.length >= 50) {
            setDescriptionLoading(false);
            if (descriptionTimeout) {
              clearTimeout(descriptionTimeout);
              setDescriptionTimeout(null);
            }
          }
          return;
        }
      } catch (apiError) {
        // API enhancement failed, try fallback
      }
      
      // Try fallback services
      try {
        let fallbackEnhanced;
        if (property.status === 'sold') {
          fallbackEnhanced = await getSoldPropertyDetails(property);
        } else if (property.status === 'forRent') {
          fallbackEnhanced = await getRentPropertyDetails(property);
        } else {
          fallbackEnhanced = await getUnifiedPropertyDetails(propertyId);
        }
        
        if (fallbackEnhanced && fallbackEnhanced !== property) {
          // Upsert enhanced data to database for future use
          upsertEnhancedPropertyData(fallbackEnhanced);
          
          setProperty(fallbackEnhanced);
          
          // Clear description loading state if we got a valid description
          if (fallbackEnhanced.description && 
              fallbackEnhanced.description !== 'Property details available' &&
              fallbackEnhanced.description !== 'No description available' &&
              fallbackEnhanced.description.length >= 50) {
            setDescriptionLoading(false);
            if (descriptionTimeout) {
              clearTimeout(descriptionTimeout);
              setDescriptionTimeout(null);
            }
          }
          return;
        }
      } catch (fallbackError) {
        // Fallback enhancement failed
      }
      
    } catch (error) {
      // All enhancement methods failed
    }
  };


  useEffect(() => {
    if (!id) return;
    
    // Restore cache from localStorage on component mount
    restoreCacheFromLocalStorage();
    
    const loadProperty = async (retryCount = 0) => {
      
      // Set a maximum loading time to prevent infinite loading
      const maxLoadingTime = 30000; // 30 seconds
      const loadingTimeout = setTimeout(() => {
        if (retryCount < 2) {
          // Retry up to 2 times
          loadProperty(retryCount + 1);
        } else {
          setLoading(false);
          setError('Property loading timed out. Please try again.');
        }
      }, maxLoadingTime);
      
      try {
        
        // PRIORITY 1: Check local cache first for instant response (especially for address-based search)
        let existingProperty: Property | null = null;
        let databaseFailed = false;
        
        // PRIORITY 1: Check local cache first for instant response
        
        // Check buy/sale search cache first (for buy-search properties)
        const saleCacheKeys = Array.from(propertyCache.getStats().keys).filter(key => key.startsWith('sale-'));
        for (const cacheKey of saleCacheKeys) {
          const cachedData = propertyCache.get(cacheKey);
          if (cachedData) {
            const foundProperty = cachedData.find((p: Property) => p.source_id === id || p.id === id);
            if (foundProperty) {
              existingProperty = foundProperty;
              break;
            }
          }
        }
        
        // PRIORITY 1.5: Check localStorage if memory cache is empty (app startup scenario)
        if (!existingProperty) {
          try {
            const storedListings = localStorage.getItem('homeFilters_listings');
            if (storedListings) {
              const listings = JSON.parse(storedListings);
              if (Array.isArray(listings) && listings.length > 0) {
                const foundProperty = listings.find((p: Property) => p.source_id === id || p.id === id);
                if (foundProperty) {
                  existingProperty = foundProperty;
                }
              }
            }
          } catch (error) {
            // localStorage access failed, continue with other checks
          }
        }
        
        // Check rent search cache
        if (!existingProperty) {
          const rentCacheKeys = Array.from(propertyCache.getStats().keys).filter(key => key.startsWith('rent-'));
          for (const cacheKey of rentCacheKeys) {
            const cachedData = propertyCache.get(cacheKey);
            if (cachedData) {
              const foundProperty = cachedData.find((p: Property) => p.source_id === id || p.id === id);
              if (foundProperty) {
                existingProperty = foundProperty;
                break;
              }
            }
          }
        }
        
        // Check restored cache from localStorage
        if (!existingProperty) {
          const restoredCacheData = propertyCache.get('restored-from-localStorage');
          if (restoredCacheData) {
            const foundProperty = restoredCacheData.find((p: Property) => p.source_id === id || p.id === id);
            if (foundProperty) {
              existingProperty = foundProperty;
            }
          }
        }
        
        // Check sold search cache
        if (!existingProperty) {
          // First, try to find by property ID cache key (most efficient)
          const idCacheKey = `property-id-${id}`;
          const idCachedData = propertyCache.get(idCacheKey);
          if (idCachedData) {
            const foundProperty = idCachedData.find((p: Property) => p.id === id || p.source_id === id);
            if (foundProperty) {
              existingProperty = foundProperty;
            }
          }
          
          // If not found by ID, search through all cache entries
          if (!existingProperty) {
            const soldCacheKeys = Array.from(propertyCache.getStats().keys).filter(key => key.startsWith('sold-'));
            for (const cacheKey of soldCacheKeys) {
              const cachedData = propertyCache.get(cacheKey);
              if (cachedData) {
                const foundProperty = cachedData.find((p: Property) => p.id === id || p.source_id === id);
                if (foundProperty) {
                  existingProperty = foundProperty;
                  break;
                }
              }
            }
          }
        }
        
        // PRIORITY 2: If we found property in local cache, display it instantly and enhance in background
        if (existingProperty) {
          
          
          // Set the property immediately from cache for instant display
          setProperty(existingProperty);
          setLoading(false);
          
          // Check if this property has incomplete data and needs enhancement
          const hasIncompleteData = !existingProperty.description || 
                                   existingProperty.description === 'Property details available' ||
                                   existingProperty.description === 'No description available' ||
                                   existingProperty.description.length < 50 || // Very short descriptions are likely incomplete
                                   !(existingProperty as any).daysOnZillow ||
                                   !(existingProperty as any).daysOnMarket ||
                                   !existingProperty.photos ||
                                   existingProperty.photos.length === 0;
          
          // Check if description needs loading
          const needsDescriptionLoading = !existingProperty.description || 
                                        existingProperty.description === 'Property details available' ||
                                        existingProperty.description === 'No description available' ||
                                        existingProperty.description.length < 50;
          
          if (needsDescriptionLoading) {
            // Start description loading state with 5-second timeout
            setDescriptionLoading(true);
            const timeout = setTimeout(() => {
              setDescriptionLoading(false);
            }, 5000);
            setDescriptionTimeout(timeout);
          }
          
          if (hasIncompleteData) {
            // Enhance property in background without blocking the UI
            enhancePropertyInBackground(existingProperty, id);
          }
          
          // Clear timeout on success
          clearTimeout(loadingTimeout);
          return; // Exit early - we have the property from cache
        }
        
        // PRIORITY 3: Check database backup as fallback (only if not found in local cache)
        if (!existingProperty) {
          
          try {
            // Try database backup lookup with timeout (read-only, doesn't affect local cache)
            const foundBuyProperty = await Promise.race([
              getPropertiesFromDatabaseBackup(id, 'sale'),
              new Promise<Property | null>((_, reject) => 
                setTimeout(() => reject(new Error('Database backup lookup timed out')), 5000)
              )
            ]);
            
            if (foundBuyProperty) {
              
              // MERGE database data with any existing local cache data
              if (existingProperty) {
                const localProperty = existingProperty as Property;
                
                existingProperty = {
                  ...foundBuyProperty, // Start with database data as base
                  ...localProperty, // OVERLAY local cache data to preserve important data
                  // CRITICAL: Always preserve local cache photos (they are usually more complete)
                  photos: localProperty.photos && localProperty.photos.length > 0 ? localProperty.photos : foundBuyProperty.photos,
                  // CRITICAL: Always preserve local cache description (even if it's generic)
                  description: localProperty.description || foundBuyProperty.description,
                  // CRITICAL: Always preserve local cache days on market data
                  daysOnMarket: (localProperty as any).daysOnMarket || (foundBuyProperty as any).daysOnMarket,
                  daysOnZillow: localProperty.daysOnZillow || foundBuyProperty.daysOnZillow,
                  timeOnZillow: localProperty.timeOnZillow || foundBuyProperty.timeOnZillow,
                  // CRITICAL: Preserve all other local cache data that might be missing from database
                  price: localProperty.price || foundBuyProperty.price,
                  details: localProperty.details || foundBuyProperty.details,
                  address: localProperty.address || foundBuyProperty.address,
                  location: localProperty.location || foundBuyProperty.location
                };
              } else {
                existingProperty = foundBuyProperty;
              }
            } else {
              // Try sold properties database backup
              const foundSoldProperty = await Promise.race([
                getPropertiesFromDatabaseBackup(id, 'sold'),
                new Promise<Property | null>((_, reject) => 
                  setTimeout(() => reject(new Error('Sold properties backup lookup timed out')), 3000)
                )
              ]);
            
              if (foundSoldProperty) {
                // MERGE database data with any existing local cache data
                if (existingProperty) {
                  const localProperty = existingProperty as Property;
                  
                  existingProperty = {
                    ...foundSoldProperty, // Start with database data as base
                    ...localProperty, // OVERLAY local cache data to preserve important data
                    // CRITICAL: Always preserve local cache photos (they are usually more complete)
                    photos: localProperty.photos && localProperty.photos.length > 0 ? localProperty.photos : foundSoldProperty.photos,
                    // CRITICAL: Always preserve local cache description (even if it's generic)
                    description: localProperty.description || foundSoldProperty.description,
                    // CRITICAL: Always preserve local cache days on market data
                    daysOnMarket: (localProperty as any).daysOnMarket || (foundSoldProperty as any).daysOnMarket,
                    daysOnZillow: localProperty.daysOnZillow || foundSoldProperty.daysOnZillow,
                    timeOnZillow: localProperty.timeOnZillow || foundSoldProperty.timeOnZillow,
                    // CRITICAL: Preserve all other local cache data that might be missing from database
                    price: localProperty.price || foundSoldProperty.price,
                    details: localProperty.details || foundSoldProperty.details,
                    address: localProperty.address || foundSoldProperty.address,
                    location: localProperty.location || foundSoldProperty.location
                  };
                } else {
                  existingProperty = foundSoldProperty;
                }
              }
            }
          } catch (dbError) {
            databaseFailed = true;
          }
        }
        
        // PRIORITY 3: If not found in any cache, create a minimal property and call details API
        
        if (!existingProperty) {
          
          // Create a minimal property with unknown status - we'll determine it from the API
          const propertyToEnrich: Property = {
            id: id,
            source_id: id,
            title: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            price: 0,
            details: { bedrooms: 0, bathrooms: 0, squareFeet: 0, yearBuilt: 0, lotSize: { amount: 0 } },
            type: 'singleFamily',
            photos: [],
            description: '',
            location: { lat: 0, lon: 0 },
            url: '',
            last_sync: new Date().toISOString(),
            source: 'zillow',
            
            daysOnZillow: null,
            timeOnZillow: null
          };
          
          existingProperty = propertyToEnrich;
          
          // If database failed, handle gracefully
          if (databaseFailed) {
          }
        }
        
        // Display property from database cache or create minimal property
        if (existingProperty) {
          
          // Set the property immediately for display
          setProperty(existingProperty);
          setLoading(false);
          
          // Check if this property needs enhancement
          const hasIncompleteData = !existingProperty.description || 
                                   existingProperty.description === 'Property details available' ||
                                   existingProperty.description === 'No description available' ||
                                   existingProperty.description.length < 50 ||
                                   !(existingProperty as any).daysOnZillow ||
                                   !(existingProperty as any).daysOnMarket ||
                                   !(existingProperty as any).days_on_zillow ||
                                   !(existingProperty as any).timeOnZillow ||
                                   !(existingProperty as any).time_on_zillow ||
                                   !existingProperty.photos ||
                                   existingProperty.photos.length === 0;
          
          // Check if description needs loading
          const needsDescriptionLoading = !existingProperty.description || 
                                        existingProperty.description === 'Property details available' ||
                                        existingProperty.description === 'No description available' ||
                                        existingProperty.description.length < 50;
          
          if (needsDescriptionLoading) {
            // Start description loading state with 5-second timeout
            setDescriptionLoading(true);
            const timeout = setTimeout(() => {
              setDescriptionLoading(false);
            }, 5000);
            setDescriptionTimeout(timeout);
          }
          
          if (hasIncompleteData) {
            // Enhance property in background without blocking the UI
            enhancePropertyInBackground(existingProperty, id);
          }
          
          // Clear timeout on success
          clearTimeout(loadingTimeout);
          return; // Exit early - we have the property
        }
        
        // PRIORITY 4: If no property found anywhere, create minimal fallback
        const fallbackProperty: Property = {
          id: id,
          source_id: id,
          title: 'Property Details',
          address: 'Address not available',
          city: '',
          state: '',
          zipCode: '',
          price: 0,
          details: { bedrooms: 0, bathrooms: 0, squareFeet: 0, yearBuilt: 0, lotSize: { amount: 0 } },
          type: 'singleFamily',
          status: 'forSale',
          photos: [],
          description: 'Property details are currently unavailable. Please try refreshing the page or check back later.',
          location: { lat: 0, lon: 0 },
          url: '',
          last_sync: new Date().toISOString(),
          source: 'unknown',
          
          daysOnZillow: null,
          timeOnZillow: null
        };
        
        setProperty(fallbackProperty);
        setLoading(false);
        clearTimeout(loadingTimeout);
        
      } catch (error) {
        
        if (retryCount < 2) {
          // Retry up to 2 times on error
          clearTimeout(loadingTimeout);
          setTimeout(() => loadProperty(retryCount + 1), 1000 * (retryCount + 1)); // Exponential backoff
          return;
        } else {
          setError(error instanceof Error ? error.message : 'Failed to load property');
        }
      } finally {
        // Clear the timeout and ensure loading is set to false
        clearTimeout(loadingTimeout);
        if (retryCount >= 2) {
          setLoading(false);
        }
      }
    };
    
    loadProperty();
  }, [id]);



  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Property</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="flex gap-2 justify-center">
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
            <button 
              onClick={() => {
                clearAllCaches();
                localStorage.removeItem('homeFilters_listings');
                window.location.reload();
              }} 
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Clear All Caches & Reload
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return <div className="flex justify-center items-center h-screen">Property not found.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex flex-col items-center py-4 px-1">
            {/* Photo Carousel */}
      <div className="w-full max-w-md mx-auto rounded-b-none rounded-t-2xl overflow-hidden shadow-md relative">
        {/* Back Button - Top Left Corner */}
        <button
          onClick={() => window.history.length > 1 ? window.history.back() : window.location.assign('/')}
          className="absolute left-3 top-3 z-20 bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="h-5 w-5 text-blue-700" />
        </button>
        
        {property.photos && property.photos.length > 0 ? (
          <div className="relative">
            <Carousel className="w-full h-64">
              <CarouselContent>
                {property.photos.map((url, idx) => (
                  <CarouselItem key={idx} className="flex items-center justify-center h-64">
                    <img
                      src={url}
                      alt={`Property photo ${idx + 1}`}
                      className="object-cover w-full h-full cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handlePhotoClick(idx)}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/placeholder.jpg'; // Make sure to add this image to your public directory
                      }}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
            
            {/* Click to view fullscreen indicator */}
            <div className="absolute bottom-5 right-3 bg-black bg-opacity-50 text-white rounded-full p-2 pointer-events-none">
              <Camera className="h-4 w-4" />
            </div>
          </div>
        ) : (
          <div className="h-64 bg-gray-200 flex items-center justify-center">
            <span className="text-gray-400">No photos available</span>
          </div>
        )}
      </div>

      {/* Main Card */}
      <Card className="w-full max-w-md mx-auto rounded-t-none rounded-b-2xl -mt-4 z-10 shadow-lg">
        <CardContent className="p-6">
          {/* Price and Title */}
          <div className="flex flex-col gap-1 mb-2">
            {/* Display price range if available, otherwise show unavailable */}
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold text-blue-700">
                {property.status === 'sold' ? getSoldDisplayPrice(property) : getRentDisplayPrice(property)}
              </span>
              
              {/* Sold Date Pill - Next to Price */}
              {property.status === 'sold' && (() => {
                // Check multiple locations for sold date
                let soldDate = null;
                
                // Check soldPropertyMetadata first
                if ((property as any).soldPropertyMetadata?.soldDate) {
                  soldDate = (property as any).soldPropertyMetadata.soldDate;
                }
                // Check if we have the raw API response with formattedChip.additionalFacts
                else if ((property as any).formattedChip?.additionalFacts) {
                  const soldDateFact = (property as any).formattedChip.additionalFacts.find((fact: any) => 
                    fact.elementType === 'soldOnDate' && fact.value?.fullValue
                  );
                  if (soldDateFact) {
                    soldDate = soldDateFact.value.fullValue;
                  }
                }
                // Check for any other sold date fields
                else if ((property as any).soldDate) {
                  soldDate = (property as any).soldDate;
                } else if ((property as any).sold_date) {
                  soldDate = (property as any).sold_date;
                }
                
                return soldDate ? (
                  <div className="bg-blue-700 text-white text-xs font-semibold rounded-full px-3 py-1 shadow">
                    Sold {soldDate}
                  </div>
                ) : null;
              })()}
              
              {/* Days on Market Pill - Next to Price for Buy Properties */}
              {(property.status === 'forSale' || property.status === 'for_sale' || property.status === 'active') && (() => {
                // Check multiple locations for days on market
                let daysOnMarket = null;
                
                
                // Check for daysOnZillow field
                if ((property as any).daysOnZillow && (property as any).daysOnZillow > 0) {
                  daysOnMarket = (property as any).daysOnZillow;
                }
                // Check for daysOnMarket field
                else if ((property as any).daysOnMarket && (property as any).daysOnMarket > 0) {
                  daysOnMarket = (property as any).daysOnMarket;
                }
                // Check for days_on_market field
                else if ((property as any).days_on_market && (property as any).days_on_market > 0) {
                  daysOnMarket = (property as any).days_on_market;
                }
                // Check for timeOnZillow field
                else if ((property as any).timeOnZillow && (property as any).timeOnZillow > 0) {
                  daysOnMarket = (property as any).timeOnZillow;
                }
                // Check for time_on_zillow field
                else if ((property as any).time_on_zillow && (property as any).time_on_zillow > 0) {
                  daysOnMarket = (property as any).time_on_zillow;
                }
                
                
                return daysOnMarket ? (
                  <div className="bg-green-600 text-white text-xs font-semibold rounded-full px-3 py-1 shadow">
                    {daysOnMarket} Days on Market
                  </div>
                ) : null;
              })()}
              
              {/* Days on Market Pill - Next to Price for Rental Properties (only show when data is from details API) */}
              {property.status === 'forRent' && (() => {
                // Only show days on market pill if we have reliable data from the details API
                // Check if this property has been enhanced with details API data
                const hasDetailsApiData = property.description && 
                                        property.description !== 'Property details available' && 
                                        property.description !== 'No description available' &&
                                        property.description.length > 50;
                
                if (!hasDetailsApiData) {
                  // Don't show the pill if we don't have details API data yet
                  return null;
                }
                
                // Check multiple locations for days on market
                let daysOnMarket = null;
                
                // Check for daysOnZillow field
                if ((property as any).daysOnZillow && (property as any).daysOnZillow > 0) {
                  daysOnMarket = (property as any).daysOnZillow;
                }
                // Check for daysOnMarket field
                else if ((property as any).daysOnMarket && (property as any).daysOnMarket > 0) {
                  daysOnMarket = (property as any).daysOnMarket;
                }
                // Check for days_on_market field
                else if ((property as any).days_on_market && (property as any).days_on_market > 0) {
                  daysOnMarket = (property as any).days_on_market;
                }
                // Check for timeOnZillow field
                else if ((property as any).timeOnZillow && (property as any).timeOnZillow > 0) {
                  daysOnMarket = (property as any).timeOnZillow;
                }
                // Check for time_on_zillow field
                else if ((property as any).time_on_zillow && (property as any).time_on_zillow > 0) {
                  daysOnMarket = (property as any).time_on_zillow;
                }
                // Check for listingDateTimeOnZillow field (rental-specific) - only if we have details API data
                else if ((property as any).rentalMetadata?.listingDateTimeOnZillow && (property as any).rentalMetadata.listingDateTimeOnZillow > 0) {
                  // Calculate days since listing
                  const listingDate = new Date((property as any).rentalMetadata.listingDateTimeOnZillow);
                  const now = new Date();
                  const diffTime = Math.abs(now.getTime() - listingDate.getTime());
                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  if (diffDays > 0) {
                    daysOnMarket = diffDays;
                  }
                }
                
                // Only show the pill if we have valid days on market data
                return daysOnMarket ? (
                  <div className="bg-green-600 text-white text-xs font-semibold rounded-full px-3 py-1 shadow">
                    {daysOnMarket} Days on Market
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Display property title prominently - hide for sold properties */}
            {property.status !== 'sold' && property.title && (
              <span className="text-xl font-semibold text-gray-900">
                {property.title}
              </span>
            )}
            
            {/* Display street address below the title */}
            <span className="text-lg text-gray-700 font-medium">
              {typeof property.address === 'string' 
                ? property.address 
                : (typeof property.address === 'object' && property.address !== null
                    ? ((property.address as any)?.streetAddress || (property.address as any)?.street || 'Address not available')
                    : property.address || 'Address not available')}
            </span>
            
            {/* Display city, state, zip below the street address */}
            <span className="flex items-center text-gray-500 text-sm gap-1">
              <MapPin className="h-4 w-4" />
              {typeof property.address === 'object' && property.address !== null
                ? `${(property.address as any)?.city || ''}, ${(property.address as any)?.state || ''} ${(property.address as any)?.zipcode || ''}`
                : `${property.city || ''}, ${property.state || ''} ${property.zipCode || ''}`}
            </span>
          </div>

          {/* Key Facts Row */}
          <div className="flex justify-between items-start my-4 px-1" style={{ minHeight: '90px' }}>
            <div className="flex flex-col items-center flex-1 text-xs">
              <BedDouble className="h-5 w-5 mb-1 text-blue-500" />
              <span>
                {(() => {
                  // For land/lots, always show - for bed/bath
                  const isLand = property.type === 'Land' || property.type === 'land' || 
                               (typeof property.type === 'string' && property.type.toLowerCase().includes('land'));
                  if (isLand) {
                    return '- Beds';
                  }
                  
                  // Check if we have bedroom range information
                  const rentalMetadata = (property as any).rentalMetadata;
                  
                  if (rentalMetadata?.unitsGroup && Array.isArray(rentalMetadata.unitsGroup)) {
                    const bedroomCounts = rentalMetadata.unitsGroup
                      .map((unit: any) => unit.bedrooms)
                      .filter((beds: any) => beds !== undefined && beds !== null)
                      .sort((a: any, b: any) => a - b);
                    
                    if (bedroomCounts.length > 0) {
                      const minBeds = Math.min(...bedroomCounts);
                      const maxBeds = Math.max(...bedroomCounts);
                      
                      if (minBeds === maxBeds) {
                        return `${minBeds} Bed`;
                      } else {
                        return `${minBeds}-${maxBeds} Beds`;
                      }
                    }
                  }
                  
                  // Fallback to single bedroom count
                  const bedroomCount = property.details?.bedrooms;
                  return bedroomCount && bedroomCount > 0 ? `${bedroomCount} Bed` : '- Beds';
                })()}
              </span>
              {property.hoa_dues?.amount && Number(property.hoa_dues.amount) > 0 ? (
                <div className="flex flex-col items-center mt-2">
                  <Receipt className="h-5 w-5 mb-1 text-blue-500" />
                  <span>{`$${typeof property.hoa_dues.amount === 'number' ? property.hoa_dues.amount.toLocaleString() : Number(property.hoa_dues.amount || 0).toLocaleString()}/mo HOA`}</span>
                </div>
              ) : property.status === 'sold' || property.status === 'forRent' ? (
                <div className="flex flex-col items-center mt-2">
                  <Receipt className="h-5 w-5 mb-1 text-blue-500" />
                  <span>- HOA</span>
                </div>
              ) : (
                <div className="flex flex-col items-center mt-2">
                  <Receipt className="h-5 w-5 mb-1 text-blue-500" />
                  <span>No HOA</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-center flex-1 text-xs">
              <Bath className="h-5 w-5 mb-1 text-blue-500" />
              <span>
                {(() => {
                  // For land/lots, always show - for bed/bath
                  const isLand = property.type === 'Land' || property.type === 'land' || 
                               (typeof property.type === 'string' && property.type.toLowerCase().includes('land'));
                  if (isLand) {
                    return '- Baths';
                  }
                  
                  // Check if we have bathroom range information
                  const rentalMetadata = (property as any).rentalMetadata;
                  
                  if (rentalMetadata?.unitsGroup && Array.isArray(rentalMetadata.unitsGroup)) {
                    const bathroomCounts = rentalMetadata.unitsGroup
                      .map((unit: any) => unit.bathrooms)
                      .filter((baths: any) => baths !== undefined && baths !== null)
                      .sort((a: any, b: any) => a - b);
                    
                    if (bathroomCounts.length > 0) {
                      const minBaths = Math.min(...bathroomCounts);
                      const maxBaths = Math.max(...bathroomCounts);
                      
                      if (minBaths === maxBaths) {
                        return `${minBaths} Bath`;
                      } else {
                        return `${minBaths}-${maxBaths} Baths`;
                      }
                    }
                  }
                  
                  // Fallback to single bathroom count
                  const bathroomCount = property.details?.bathrooms;
                  return bathroomCount && bathroomCount > 0 ? `${bathroomCount} Bath` : '- Baths';
                })()}
              </span>
            </div>
            <div className="flex flex-col items-center flex-1 text-xs">
              <Ruler className="h-5 w-5 mb-1 text-blue-500" />
              <span>{property.details?.squareFeet && typeof property.details.squareFeet === 'number' ? property.details.squareFeet.toLocaleString() : '-'} sqft</span>
            </div>
            <div className="flex flex-col items-center flex-1 text-xs">
              <Calendar className="h-5 w-5 mb-1 text-blue-500" />
              <span title="Year Built">
                {property.details?.yearBuilt && property.details.yearBuilt > 0 ? property.details.yearBuilt : '-'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between gap-2 mt-4 mb-4">
            {(() => {
              const favId = String(((property as any)?.source_id) || property?.id || '');
              const isFavorited = favId ? favorites.includes(favId) : false;
              return (
                <button
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-semibold border transition-colors ${
                    isFavorited
                      ? 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100'
                      : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                  }`}
                  onClick={() => { if (favId) { toggleFavorite(favId); } }}
                  aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                  title={isFavorited ? 'Saved' : 'Save'}
                >
                  <Heart
                    className={`h-5 w-5 ${isFavorited ? 'text-pink-600' : ''}`}
                    fill={isFavorited ? 'currentColor' : 'none'}
                    stroke={isFavorited ? 'currentColor' : 'currentColor'}
                  />
                  {isFavorited ? 'Saved' : 'Save'}
                </button>
              );
            })()}
            <button
              className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-blue-50 text-blue-700 font-semibold border border-blue-200 hover:bg-blue-100"
              onClick={() => {
                const targetId = String(((property as any)?.source_id) || property?.id || '');
                if (!targetId) return;
                const lat = (property as any)?.location?.lat ?? (property as any)?.lat ?? null;
                const lng = (property as any)?.location?.lon ?? (property as any)?.lon ?? null;
                const addr = typeof property.address === 'string' ? property.address : (
                  typeof property.address === 'object' && property.address ? ((property.address as any).streetAddress || (property.address as any).street || '') : ''
                );
                const params = new URLSearchParams({ focus: targetId });
                if (typeof lat === 'number' && typeof lng === 'number') {
                  params.set('lat', String(lat));
                  params.set('lng', String(lng));
                }
                if (addr) params.set('addr', addr);
                setLocation(`/map?${params.toString()}`);
              }}
              aria-label="View on map"
              title="Map"
            >
              <MapIcon className="h-5 w-5" /> Map
            </button>
          </div>

          {/* Description Section */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-1">
              Description
            </h3>
            {(() => {
              // Show loading state if description is loading
              if (descriptionLoading) {
                return (
                  <div className="space-y-2">
                    <p className="text-gray-500 text-sm italic">Loading description...</p>
                  </div>
                );
              }
              
              // Filter description at render time to ensure blacklist is enforced on cached data
              const displayDescription = filterZestimateDescriptions(property.description);
              
              if (displayDescription && displayDescription.length > 0) {
                return (
                  <div className="space-y-2">
                    <p className="text-gray-700 text-sm">{displayDescription}</p>
                    {/* Add message for all rental properties */}
                    {property.status === 'forRent' && (
                      <p className="text-blue-600 text-sm font-medium italic">
                        Multiple units at varying rates may be available.
                      </p>
                    )}
                  </div>
                );
              } else {
                // Enhanced fallback with more helpful information
                return (
                  <div className="space-y-2">
                    <p className="text-gray-400 text-sm italic">No description available for this property.</p>
                    {property.status === 'forSale' && (
                      <p className="text-blue-600 text-sm font-medium">
                        This appears to be a property for sale. Property details are being loaded from our database and external sources.
                      </p>
                    )}
                    {property.status === 'sold' && (
                      <p className="text-blue-600 text-sm font-medium">
                        This property has been sold. Historical details may be limited.
                      </p>
                    )}
                    {property.status === 'forRent' && (
                      <p className="text-blue-600 text-sm font-medium">
                        This is a rental property. Unit details are being loaded from our database.
                      </p>
                    )}

                  </div>
                );
              }
            })()}
          </div>

          {/* Map Preview */}
          {(() => {
            // Calculate map URL only when property exists
            if (property) {
              // Check for coordinates in multiple possible locations
              let lat = 0;
              let lon = 0;
              
              // Priority 1: Check the location object
              if (property.location && typeof property.location === 'object') {
                lat = property.location.lat || 0;
                lon = property.location.lon || 0;
              }
              
              // Priority 2: Check top-level latitude/longitude fields
              if (lat === 0 && lon === 0) {
                lat = (property as any).latitude || 0;
                lon = (property as any).longitude || 0;
              }
              
              // Priority 3: Check if coordinates are stored as strings and convert
              if (lat && lon) {
                lat = typeof lat === 'string' ? parseFloat(lat) : lat;
                lon = typeof lon === 'string' ? parseFloat(lon) : lon;
              }
              

              
              if (lat && lon && !isNaN(lat) && !isNaN(lon) && lat !== 0 && lon !== 0) {

                
                return (
                  <div className="mb-2 rounded-lg overflow-hidden border border-gray-200">
                    {/* OpenStreetMap with Leaflet */}
                    <PropertyMap 
                      latitude={lat}
                      longitude={lon}
                      address={property.address}
                      className="w-full"
                      initialZoom={14}
                      markers={[{ lat, lng: lon, label: (typeof property.address === 'string' ? property.address : ((property.address as any)?.streetAddress ?? (property.address as any)?.street ?? 'Property')) }]}
                    />
                    

                    

                    

                  </div>
                );
              } else {
                return null;
              }
            }
            return null;
          })()}
        </CardContent>
      </Card>

      {/* Photo Viewer Modal */}
      {property.photos && property.photos.length > 0 && (
        <PhotoViewer
          photos={property.photos}
          isOpen={photoViewerOpen}
          onClose={() => setPhotoViewerOpen(false)}
          initialIndex={selectedPhotoIndex}
        />
      )}
    </div>
  );
}